import { inject, Injectable } from '@angular/core';
import { catchError, EMPTY, firstValueFrom, map, Observable, of, shareReplay, throwError } from 'rxjs';
import { Page } from '../../../@common/entities/page.interface';
import { Logger } from '../../../@common/log/logger';
import { Category, CategoryInfo, CategoryInfoSort, CategorySort, Language, ManageCategoryBody, SortOrder, V2CategoriesResource, VideoInfo, VideoInfoSort, VideoType } from '../../../@generated';
import { NavigationService } from '../../navigation/application/navigation.service';

@Injectable({
    providedIn: 'root',
})
export class CategoryService {
    private logger = inject(Logger);
    private categoriesResource = inject(V2CategoriesResource);
    private navigationService = inject(NavigationService);

    public getCategories(
        page: number,
        pageSize: number,
        order: SortOrder,
        sort: CategorySort,
    ): Observable<Page<Category>> {
        return this.categoriesResource.getCategories(page, pageSize, order, sort).pipe(
            shareReplay(1),
            catchError(e => {
                this.logger.apiError('Get all categories failed', e);
                return of({ count: 0, results: [] });
            })
        );
    }

    public getAllCategoriesInfos(): Observable<CategoryInfo[]> {
        return this.getCategoriesInfos(1, 999999, SortOrder.Ascending, CategoryInfoSort.Name, undefined).pipe(
            map(response => response.results),
            catchError(e => {
                this.logger.apiError('Get all categories infos failed', e);
                return of([]);
            })
        );
    }

    public getCategoriesInfos(
        page: number,
        pageSize: number,
        order: SortOrder | undefined,
        sort: CategoryInfoSort | undefined,
        query: string | undefined
    ): Observable<Page<CategoryInfo>> {
        return this.categoriesResource.getCategoriesInfos(page, pageSize, order, sort, query).pipe(
            catchError(e => {
                this.logger.apiError('Get category infos failed', e);
                return of({ count: 0, results: [] });
            })
        );
    }

    public getCategoryVideos(
        id: number,
        page: number,
        pageSize: number,
        sort: VideoInfoSort | undefined,
        language: Language | undefined,
        order: SortOrder | undefined,
        type: VideoType | undefined,
        year: number | undefined
    ): Observable<Page<VideoInfo>> {
        return this.categoriesResource.getCategoryVideos(id, page, pageSize, order, year, language, type, sort)
            .pipe(
                shareReplay(1),
                catchError(e => {
                    this.logger.apiError('Get videos of category failed', e);
                    return of({ count: 0, results: [] });
                })
            );
    }

    public getCategory(id: number): Observable<Category> {
        return this.categoriesResource.getCategoryById(id).pipe(
            catchError(e => {
                if (e.status === 404) {
                    this.navigationService.navigateToNotFound();
                } else {
                    this.logger.apiError('Get category failed', e);
                }

                return EMPTY;
            })
        );
    }

    public createCategory(body: ManageCategoryBody) {
        return firstValueFrom(this.categoriesResource.createCategory(body).pipe(
            catchError(e => {
                this.logger.apiError('Create category failed', e);
                return throwError(() => e);
            })
        ))
    }

    public updateCategory(id: number, body: ManageCategoryBody) {
        return firstValueFrom(this.categoriesResource.updateCategory(id, body).pipe(
            catchError(e => {
                this.logger.apiError('Update category failed', e);
                return throwError(() => e);
            })
        ));
    }

    public deleteCategory(id: number) {
        return firstValueFrom(this.categoriesResource.deleteCategory(id).pipe(
            catchError(e => {
                this.logger.apiError('Delete category failed', e);
                return throwError(() => e);
            })
        ));
    }
}
